<template>
  <v-dialog v-model="dialog" scrollable persistent max-width="500px" transition="dialog-transition">
    <template #activator="{ on, attrs }">
      <v-btn small outlined v-on="on" v-bind="attrs" color="primary" class="text-none ml-4">
        {{ thisShift }}, {{ thisStart }} - {{ thisEnd }}
      </v-btn>
    </template>
    <v-form v-model="isValid" @submit.prevent="apply">
      <v-card>
        <v-card-text>
          <v-menu v-model="menu" :close-on-content-click="false"
           :nudge-right="40" transition="scale-transition"
            offset-y min-width="auto">
            <template #activator="{ on, attrs }">
              <v-text-field v-model="date" :label="$t('date')"
               outlined required prepend-icon="mdi-calendar" readonly
                v-bind="attrs" v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="dateRange" :max="today"
             multiple no-title scrollable :locale="locale"
              @input="onDateRangeSelect" />

          </v-menu>
          <div class="d-flex align-center">
            <v-btn small text color="primary" class="mr-2" @click="selectAllShifts">
            </v-btn>
            <v-btn small text color="primary" @click="deselectAllShifts">
            </v-btn>
          </div>
          <v-select :items="shifts" v-model="shift" multiple :label="$t('shift')"
           outlined class="ml-8 mt-2" chips>
            <!-- ✅ Select All / Deselect All Icon Slot -->
            <template v-slot:prepend-item>
              <v-list-item ripple @mousedown.prevent @click="toggleShifts">
                <v-list-item-action>
                  <v-icon :color="shift.length ? 'indigo darken-4' : ''">
                    {{ shiftIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ shift.length === shifts.length ? 'Deselect All' : 'Select All' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>

            <!-- ✅ Custom summary for selection -->
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ shift[0] }}
                <span v-if="shift.length > 1"> (+{{ shift.length - 1 }} more)</span>
              </span>
            </template>
          </v-select>

          <v-select :items="availableLineNames" v-model="lineNames"
           multiple :label="$t('line name')" outlined
            class="ml-8 mt-2" chips @change="onLineChange">
            <!-- ✅ Select All / Deselect All Icon Slot -->
            <template v-slot:prepend-item>
              <v-list-item ripple @mousedown.prevent @click="toggleLineNames">
                <v-list-item-action>
                  <v-icon :color="lineNames.length ? 'indigo darken-4' : ''">
                    {{ lineNameIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ lineNames.length === availableLineNames.length ? 'Deselect All' :
                     'Select All' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <!-- ✅ Custom selection summary display -->
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ lineNames[0] }}
                <span v-if="lineNames.length > 1"> (+{{ lineNames.length - 1 }} more)</span>
              </span>
            </template>
          </v-select>
          <v-select :items="availableMachineNames" v-model="machineNames" multiple
           :label="$t('machine name')" outlined
            class="ml-8 mt-2" chips @change="onMachineChange">
            <!-- ✅ Toggle Select/Deselect All Icon -->
            <template v-slot:prepend-item>
              <v-list-item ripple @mousedown.prevent @click="toggleMachineNames">
                <v-list-item-action>
                  <v-icon :color="machineNames.length ? 'indigo darken-4' : ''">
                    {{ machineNameIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ machineNames.length === availableMachineNames.length ? 'Deselect All' :
                     'Select All' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ machineNames[0] }}
                <span v-if="machineNames.length > 1"> (+{{ machineNames.length - 1 }} more)</span>
              </span>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" class="text-none" @click="dialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn type="submit" color="primary" :disabled="!isValid
           || disableApply" class="text-none">
            {{ $t('apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

export default {
  name: 'ShiftSelector',
  data() {
    return {
      lineNames: [],
      machineNames: [],
      dateRange: [], // store multiple selected dates
      shift: null,
      date: null,
      today: new Date().toISOString().substr(0, 10),
      menu: false,
      isValid: false,
      dialog: false,
    };
  },
  created() {
    const currentDate = this.formattedDate(this.currentDate.toString());
    // this.shift = this.currentShift;
    this.shift = [this.currentShift]; // wrap single shift in an array
    // this.date = currentDate;
    this.date = currentDate;
    this.dateRange = [currentDate]; // Default to a single-date range
    this.setThisStart(currentDate);
    this.setThisEnd(currentDate);
    this.setFilterMachine(this.machineNames);
    this.lineNames = [...this.availableLineNames];
    this.setThisLine(this.lineNames.join(','));
    // this.updateMachineOptions();
    this.setFilters();
    this.getDashboardData();
  },
  computed: {
    ...mapState('kpiDashboard', [
      'machinerecords',
      'thisShift',
      'thisDate',
      'thisStart',
      'thisEnd',
      'previousShift',
      'previousDate',
      'currentShift',
      'currentDate',
    ]),
    ...mapGetters('kpiDashboard', ['shiftList']),
    ...mapGetters('helper', ['locale']),
    lineNameIcon() {
      return this.lineNames.length === this.availableLineNames.length
        ? 'mdi-checkbox-marked'
        : 'mdi-checkbox-blank-outline';
    },
    shiftIcon() {
      return this.shift.length === this.shifts.length
        ? 'mdi-checkbox-marked'
        : 'mdi-checkbox-blank-outline';
    },
    machineNameIcon() {
      return this.machineNames.length === this.availableMachineNames.length
        ? 'mdi-checkbox-marked'
        : 'mdi-checkbox-blank-outline';
    },

    icon() {
      return this.linename.length === this.lineNameOptions.length
        ? 'mdi-checkbox-marked'
        : 'mdi-checkbox-blank-outline';
    },
    shifts() {
      let shifts = [...this.shiftList];
      const currentDate = this.formattedDate(this.currentDate.toString());
      if (this.date === currentDate) {
        const currentIndex = shifts.findIndex((s) => s === this.currentShift);
        if (currentIndex > -1) {
          shifts = shifts.filter((s, i) => i <= currentIndex);
        }
      }
      return shifts;
    },
    disableApply() {
      return !this.shift || this.shift.length === 0;
    },
    availableLineNames() {
      const cells = this.machinerecords.map((m) => m.machinecell);
      return [...new Set(cells)].sort(); // Unique + sorted
    },
    availableMachineNames() {
      // Filter based on selected lineNames
      if (this.lineNames.length === 0) return [];
      return this.machinerecords
        .filter((m) => this.lineNames.includes(m.machinecell))
        .map((m) => m.machinename)
        .sort();
    },
  },
  watch: {
    machinerecords: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          if (!this.machineNames.length) {
            this.lineNames = [...this.availableLineNames];
            this.updateMachineOptions();
          }

          this.$nextTick(() => {
            this.setFilterMachine(this.machineNames);
            this.setThisLine(this.lineNames.join(','));
          });
        }
      },
      immediate: true,
    },

  },
  methods: {
    ...mapMutations('kpiDashboard', [
      'setThisShift',
      'setThisLine',
      'setThisDate',
      'setThisStart',
      'setThisEnd',
      'setPreviousShift',
      'setPreviousDate',
      'setFilterMachine',
    ]),
    ...mapActions('kpiDashboard', ['getDashboardData']),
    onLineChange() {
      this.updateMachineOptions(); // Refines machine options based on line
    },

    onMachineChange() {
      this.setFilterMachine(this.machineNames); // Send only selected machines to Vuex
    },

    selectAllShifts() {
      this.shift = [...this.shifts]; // clone to avoid reactivity bugs
    },
    toggleLineNames() {
      if (this.lineNames.length === this.availableLineNames.length) {
        this.lineNames = [];
      } else {
        this.lineNames = [...this.availableLineNames];
      }
      this.updateMachineOptions();
    },
    toggleShifts() {
      if (this.shift.length === this.shifts.length) {
        this.shift = [];
      } else {
        this.shift = [...this.shifts];
      }
    },
    toggleMachineNames() {
      if (this.machineNames.length === this.availableMachineNames.length) {
        this.machineNames = [];
      } else {
        this.machineNames = [...this.availableMachineNames];
      }

      this.setFilterMachine(this.machineNames); // ✅ always update store
    },
    updateMachineOptions() {
      const filteredMachines = this.machinerecords
        .filter((m) => this.lineNames.includes(m.machinecell))
        .map((m) => m.machinename);

      this.machineNames = filteredMachines;
      this.setFilterMachine(this.machineNames); // ✅ Update store after filtering
    },
    deselectAllShifts() {
      this.shift = [];
    },
    onDateRangeSelect(selectedDates) {
      const uniqueDates = [...new Set(selectedDates)];

      if (!uniqueDates || uniqueDates.length === 0) {
        // No date selected
        this.dateRange = [];
        this.setThisStart(null);
        this.setThisEnd(null);
        this.date = null;
      } else if (uniqueDates.length === 1) {
        const selected = uniqueDates[0];
        this.dateRange = [selected];
        this.setThisStart(selected);
        this.setThisEnd(selected);
        this.date = selected;
        // Keep menu open for 2nd click
      } else if (uniqueDates.length === 2) {
        // Two dates selected → create full range
        const sorted = [...uniqueDates].sort();
        const start = new Date(sorted[0]);
        const end = new Date(sorted[1]);

        const fullRange = [];
        const temp = new Date(start);

        while (temp <= end) {
          fullRange.push(temp.toISOString().slice(0, 10)); // Format: YYYY-MM-DD
          temp.setDate(temp.getDate() + 1);
        }
        this.dateRange = fullRange;
        this.setThisStart(fullRange[0]);
        this.setThisEnd(fullRange[fullRange.length - 1]);
        // eslint-disable-next-line
        this.date = fullRange[0];
        this.menu = false;
      } else if (uniqueDates.length > 2) {
        const lastDate = uniqueDates[uniqueDates.length - 1];
        this.dateRange = [lastDate];
        this.setThisStart(lastDate);
        this.setThisEnd(lastDate);
        this.date = lastDate;
        // Keep menu open for next selection
      }
    },

    formattedDate(date) {
      const year = date.substring(0, 4);
      const month = date.substring(6, 4);
      const day = date.substring(8, 6);
      return `${year}-${month}-${day}`;
    },
    setFilters() {
      if (this.dateRange.length > 0) {
        const sortedDates = [...this.dateRange].sort();
        const startDate = sortedDates[0];
        const endDate = sortedDates[sortedDates.length - 1];
        this.setThisStart(startDate);
        this.setThisEnd(endDate);
        this.setThisDate(startDate);
      }
      const shiftString = this.shift.join(',');
      this.setThisDate(this.date);
      const lineString = this.lineNames.join(',');
      this.setThisShift(shiftString);
      this.setThisLine(lineString);
      this.setFilterMachine(this.machineNames);
      const currentIndex = this.shiftList
        .findIndex((s) => s === this.shift[0]);
      if (currentIndex === 0) {
        const newDate = new Date(this.date);
        newDate.setDate(newDate.getDate());
        const newShift = this.shiftList[this.shiftList.length - 1];
        this.setPreviousDate(newDate.toISOString().substr(0, 10));
        this.setPreviousShift(newShift);
      } else {
        this.setPreviousDate(this.date);
        this.setPreviousShift(this.shiftList[currentIndex - 1]);
      }
    },
    apply() {
      this.setFilters();
      this.getDashboardData();
      this.dialog = false;
      // Save to localStorage
      const filters = {
        date: this.date,
        dateRange: this.dateRange,
        shift: this.shift,
        lineNames: this.lineNames,
        machineNames: this.machineNames,
      };
      localStorage.setItem('dashboardFilters', JSON.stringify(filters));
    },
  },
};
</script>
